<template>
  <div class="image-upload" :class="{ 'is-limit': fileList.length >= limit }">
    <el-upload
      action="#"
      list-type="picture-card"
      :accept="acceptTypes"
      :file-list="fileList"
      :http-request="handleUpload"
      :before-upload="beforeUpload"
      :on-exceed="handleExceed"
      :on-preview="handlePreview"
      :on-remove="handleRemove"
      :limit="limit"
    >
      <i class="el-icon-plus"></i>
    </el-upload>

    <div v-if="isShowTip || isShowTip1" class="upload-tip">
      <span v-if="isShowTip">最多上传 {{ limit }} 张图片</span>
      <span v-if="isShowTip1">支持 {{ fileTypeText }}，单张不超过 {{ fileSize }}MB</span>
    </div>

    <el-dialog :visible.sync="previewVisible" append-to-body>
      <img class="preview-image" :src="previewImageUrl" alt="预览图片" />
    </el-dialog>
  </div>
</template>

<script>
import { uploadImage } from "@/api/wechat";
import { BASE_URL } from "@/utils/request";

export default {
  name: "ImageUpload",
  props: {
    value: {
      type: [String, Array],
      default: "",
    },
    limit: {
      type: Number,
      default: 1,
    },
    fileSize: {
      type: Number,
      default: 5,
    },
    fileType: {
      type: Array,
      default: () => ["png", "jpg", "jpeg"],
    },
    isShowTip: {
      type: Boolean,
      default: true,
    },
    isShowTip1: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      fileList: [],
      previewVisible: false,
      previewImageUrl: "",
    };
  },
  computed: {
    acceptTypes() {
      return this.fileType.map((type) => `.${type}`).join(",");
    },
    fileTypeText() {
      return this.fileType.join("/");
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        if (this.normalizeValue(value) === this.currentValue()) {
          return;
        }
        this.fileList = this.buildFileList(value);
      },
    },
  },
  methods: {
    beforeUpload(file) {
      const extension = this.getExtension(file.name);
      const isAllowedType =
        this.fileType.length === 0 || this.fileType.includes(extension);
      const isAllowedSize = file.size / 1024 / 1024 <= this.fileSize;

      if (!isAllowedType) {
        this.$message.error(`图片格式只能是 ${this.fileTypeText}`);
        return false;
      }

      if (!isAllowedSize) {
        this.$message.error(`图片大小不能超过 ${this.fileSize}MB`);
        return false;
      }

      return true;
    },
    async handleUpload(options) {
      try {
        const response = await uploadImage(options.file);
        if (response && response.code && response.code !== 200) {
          throw new Error(response.msg || "图片上传失败");
        }

        const rawUrl = this.extractUploadUrl(response);
        if (!rawUrl) {
          throw new Error("上传接口未返回图片地址");
        }

        const uploadFile = {
          uid: options.file.uid,
          name: options.file.name,
          url: this.toDisplayUrl(rawUrl),
          rawUrl,
        };
        const nextList =
          this.limit === 1 ? [uploadFile] : [...this.fileList, uploadFile];

        this.fileList = nextList.slice(-this.limit);
        this.emitValue();
        options.onSuccess(response, options.file);
      } catch (error) {
        this.$message.error(error.message || "图片上传失败");
        options.onError(error);
      }
    },
    handleRemove(file) {
      this.fileList = this.fileList.filter((item) => {
        return item.uid !== file.uid && item.url !== file.url;
      });
      this.emitValue();
    },
    handlePreview(file) {
      this.previewImageUrl = file.url;
      this.previewVisible = true;
    },
    handleExceed() {
      this.$message.warning(`最多上传 ${this.limit} 张图片`);
    },
    emitValue() {
      const value = this.currentValue();
      this.$emit("input", value);
      this.$emit("change", value);
    },
    buildFileList(value) {
      return this.toUrlList(value).map((url, index) => ({
        uid: `${url}-${index}`,
        name: this.getFileName(url),
        url: this.toDisplayUrl(url),
        rawUrl: url,
      }));
    },
    currentValue() {
      return this.fileList.map((file) => file.rawUrl || file.url).join(",");
    },
    normalizeValue(value) {
      return this.toUrlList(value).join(",");
    },
    toUrlList(value) {
      if (Array.isArray(value)) {
        return value
          .map((item) => (typeof item === "string" ? item : item.url))
          .filter(Boolean);
      }

      return String(value || "")
        .split(",")
        .map((url) => url.trim())
        .filter(Boolean);
    },
    extractUploadUrl(response) {
      const payloads = [
        response,
        response && response.data,
        response && response.data && response.data.data,
      ];

      for (const payload of payloads) {
        if (typeof payload === "string") {
          return payload;
        }

        if (payload && typeof payload === "object") {
          const url =
            payload.fileName ||
            payload.filePath ||
            payload.path ||
            payload.url ||
            payload.src;
          if (url) {
            return url;
          }
        }
      }

      return "";
    },
    toDisplayUrl(url) {
      if (!url || /^https?:\/\//i.test(url) || url.startsWith("data:")) {
        return url;
      }

      const normalizedUrl = url.startsWith("/") ? url : `/${url}`;
      return `${BASE_URL}${normalizedUrl}`;
    },
    getExtension(fileName) {
      return String(fileName || "")
        .split(".")
        .pop()
        .toLowerCase();
    },
    getFileName(url) {
      return String(url || "").split("/").pop() || "image";
    },
  },
};
</script>

<style scoped>
.image-upload {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.image-upload.is-limit ::v-deep .el-upload--picture-card {
  display: none;
}

.upload-tip {
  color: #909399;
  display: flex;
  flex-direction: column;
  font-size: 12px;
  line-height: 1.5;
}

.preview-image {
  display: block;
  max-width: 100%;
  width: 100%;
}
</style>
